import React, { useState } from 'react';
import { Mail, Phone, MapPin, Instagram, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { mockData, handleContactFormSubmit, handleNewsletterSignup } from '../../mock';

const INITIAL_FORM = { name: '', email: '', phone: '', message: '' };

const ContactSection = () => {
  const [contactForm, setContactForm] = useState(INITIAL_FORM);
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const handleChange = (field) => (e) =>
    setContactForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const result = handleContactFormSubmit(contactForm);
    if (result.success) {
      toast.success(result.message);
      setContactForm(INITIAL_FORM);
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    const result = handleNewsletterSignup(newsletterEmail);
    if (result.success) {
      toast.success(result.message);
      setNewsletterEmail('');
    }
  };

  return (
    <section
      id="contact"
      className="section-padding bg-subtle"
      data-testid="contact-section"
    >
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="section-title">Get In Touch</h2>
          <p className="section-subtitle">
            Ready to build lifelong capability? Let's begin the conversation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <Card className="contact-form-card">
            <CardHeader>
              <CardTitle>Send us a message</CardTitle>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                data-testid="contact-form"
              >
                <Input
                  placeholder="Your Name"
                  value={contactForm.name}
                  onChange={handleChange('name')}
                  required
                  data-testid="contact-name-input"
                />
                <Input
                  type="email"
                  placeholder="Your Email"
                  value={contactForm.email}
                  onChange={handleChange('email')}
                  required
                  data-testid="contact-email-input"
                />
                <Input
                  type="tel"
                  placeholder="Your Phone"
                  value={contactForm.phone}
                  onChange={handleChange('phone')}
                  required
                  data-testid="contact-phone-input"
                />
                <Textarea
                  placeholder="Tell us about your goals..."
                  value={contactForm.message}
                  onChange={handleChange('message')}
                  rows={4}
                  required
                  data-testid="contact-message-input"
                />
                <Button
                  type="submit"
                  className="btn-primary w-full"
                  data-testid="contact-submit-btn"
                >
                  Send Message
                  <Send className="ml-2" size={18} />
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="contact-info">
            <div className="contact-info-item">
              <Mail className="text-brand-primary" size={24} aria-hidden="true" />
              <div>
                <h4>Email</h4>
                <a
                  href={`mailto:${mockData.contact.email}`}
                  data-testid="contact-email-link"
                >
                  {mockData.contact.email}
                </a>
              </div>
            </div>

            <div className="contact-info-item">
              <Phone className="text-brand-primary" size={24} aria-hidden="true" />
              <div>
                <h4>Phone</h4>
                <a
                  href={`tel:${mockData.contact.phone}`}
                  data-testid="contact-phone-link"
                >
                  {mockData.contact.phone}
                </a>
                {mockData.contact.phone2 && (
                  <>
                    <br />
                    <a href={`tel:${mockData.contact.phone2}`}>
                      {mockData.contact.phone2}
                    </a>
                  </>
                )}
              </div>
            </div>

            <div className="contact-info-item">
              <MapPin className="text-brand-primary" size={24} aria-hidden="true" />
              <div>
                <h4>Location</h4>
                <p>{mockData.contact.address}</p>
              </div>
            </div>

            {mockData.contact.socialMedia.instagram && (
              <div className="social-links mt-8">
                <h4 className="mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  <a
                    href={mockData.contact.socialMedia.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-icon"
                    aria-label="Instagram"
                    data-testid="social-instagram-link"
                  >
                    <Instagram size={24} />
                  </a>
                </div>
              </div>
            )}

            <div className="newsletter-box mt-8">
              <h4 className="mb-2">Subscribe to our newsletter</h4>
              <p className="text-sm text-text-light mb-4">
                Get evidence-based insights on strength, mobility and lifelong capability.
              </p>
              <form
                onSubmit={handleNewsletterSubmit}
                className="flex gap-2"
                data-testid="newsletter-form"
              >
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="submit"
                  className="btn-primary"
                  data-testid="newsletter-submit-btn"
                >
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
